import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { Select } from '@/components/ui/select';
import type { Device, UpdateDeviceRequest } from '@/types/api';
import { ConnectedMeterType, DeviceType } from '@/types/api';
import {
  DeviceMultiplier,
  DeviceUnitOfMeasure,
  updateDeviceConfig,
} from '../api/devices-api';
import {
  SALT_MULTIPLIER_OPTIONS,
  buildSaltUomOptions,
  connectedMeterTypeDisplayName,
  readDeviceSaltConfig,
} from '../utils/salt-config';
import { useSaltUnitTypes } from '@/features/admin/salt-unit-types/hooks/use-salt-unit-types';

// May 14 batch:
//  - Model + Firmware inputs removed (wire field stays; UI just drops it).
//  - Location section removed — Property/Building coords carry geographic
//    context; device-level lat/lng was confusing for installers.
//  - "Mechanical Offset" relabeled to "Initial Meter Reading"; gated to SALT
//    devices only. uMeters can ship pre-calibrated but the regular-meter UI
//    no longer surfaces the field per Tanner's feedback.
//  - externalMeterModel ("Connected meter model") is SALT-specific and is
//    preserved — distinct from the (removed) device-side `model`.
export function DeviceEditModal({
  device,
  onClose,
  onSave,
}: {
  device: Device;
  onClose: () => void;
  onSave: (data: UpdateDeviceRequest) => Promise<void>;
}) {
  const [notes, setNotes] = useState(device.notes ?? '');
  // Pull dynamic display names for the UoM dropdown (May 14 batch). Falls
  // back to the hardcoded labels while the API request is in flight.
  const { data: saltUnitTypes } = useSaltUnitTypes(true);
  const uomOptions = buildSaltUomOptions(saltUnitTypes);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pulse SALT pulse-encoder config (multiplier × unit of measure). Only
  // editable for SALT transmitters; uMeter readings come straight from the
  // decoder so a manual override would just confuse the display layer.
  const isSalt = device.deviceType === DeviceType.SALTTransmitter;
  const initialSalt = isSalt ? readDeviceSaltConfig(device) : null;
  const [multiplier, setMultiplier] = useState<DeviceMultiplier>(
    initialSalt?.multiplier ?? DeviceMultiplier.OneToOne,
  );
  const [unitOfMeasure, setUnitOfMeasure] = useState<DeviceUnitOfMeasure>(
    initialSalt?.unitOfMeasure ?? DeviceUnitOfMeasure.Gallons,
  );

  // Connected meter metadata (SALT) + initial meter reading (SALT).
  const [externalMeterModel, setExternalMeterModel] = useState(device.externalMeterModel ?? '');
  const [externalMeterSerial, setExternalMeterSerial] = useState(device.externalMeterSerial ?? '');
  const [mechanicalOffset, setMechanicalOffset] = useState(
    device.mechanicalOffset != null ? String(device.mechanicalOffset) : '',
  );

  // May 15 batch — SALT connected-meter type. The fixed enum + Other +
  // free-text "Other" override pattern. Null = installer hasn't picked
  // yet (rendered as the "Connected To" placeholder).
  const [connectedMeterType, setConnectedMeterType] = useState<ConnectedMeterType | ''>(
    device.connectedMeterType ?? '',
  );
  const [connectedMeterTypeCustom, setConnectedMeterTypeCustom] = useState(
    device.connectedMeterTypeCustom ?? '',
  );

  // Custom multiplier — toggle swaps the standard 1:1/1:10/1:100/1:1000
  // dropdown for an integer input rendered as "1:N". Initialized to ON
  // when the device already carries a custom value.
  const [useCustomMultiplier, setUseCustomMultiplier] = useState(device.customPulseMultiplier != null);
  const [customMultiplier, setCustomMultiplier] = useState(
    device.customPulseMultiplier != null ? String(device.customPulseMultiplier) : '',
  );

  // Custom UoM — toggle swaps the standard dropdown for a free-text input.
  const [useCustomUnitOfMeasure, setUseCustomUnitOfMeasure] = useState(!!device.customPulseUnitOfMeasure);
  const [customUnitOfMeasure, setCustomUnitOfMeasure] = useState(device.customPulseUnitOfMeasure ?? '');

  // Re-sync inputs when the underlying `device` prop changes — e.g.,
  // background polling refetches the device after a transmission lands
  // and auto-captures the initial meter reading. Without this, useState
  // initializers only fire on mount, leaving the input empty even
  // though the parent now has the captured value.
  useEffect(() => {
    setNotes(device.notes ?? '');
    setExternalMeterModel(device.externalMeterModel ?? '');
    setExternalMeterSerial(device.externalMeterSerial ?? '');
    setMechanicalOffset(
      device.mechanicalOffset != null ? String(device.mechanicalOffset) : '',
    );
    setConnectedMeterType(device.connectedMeterType ?? '');
    setConnectedMeterTypeCustom(device.connectedMeterTypeCustom ?? '');
    setUseCustomMultiplier(device.customPulseMultiplier != null);
    setCustomMultiplier(device.customPulseMultiplier != null ? String(device.customPulseMultiplier) : '');
    setUseCustomUnitOfMeasure(!!device.customPulseUnitOfMeasure);
    setCustomUnitOfMeasure(device.customPulseUnitOfMeasure ?? '');
  }, [
    device.id,
    device.notes,
    device.externalMeterModel,
    device.externalMeterSerial,
    device.mechanicalOffset,
    device.connectedMeterType,
    device.connectedMeterTypeCustom,
    device.customPulseMultiplier,
    device.customPulseUnitOfMeasure,
  ]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // SALT pulse config rides a separate endpoint (versioned history),
      // so persist it before the main save when the values changed —
      // failing here cancels the rest so we don't end up half-saved.
      if (isSalt && initialSalt &&
        (initialSalt.multiplier !== multiplier || initialSalt.unitOfMeasure !== unitOfMeasure)) {
        await updateDeviceConfig(device.id, { multiplier, unitOfMeasure });
      }
      // Initial Meter Reading is SALT-only in the UI. For non-SALT devices
      // we leave the existing value alone (undefined → backend skips it
      // via HasValue-gated partial-update).
      let offsetNum: number | undefined;
      if (isSalt) {
        const offsetTrimmed = mechanicalOffset.trim();
        offsetNum = offsetTrimmed === '' ? undefined : Number(offsetTrimmed);
        if (offsetNum != null && Number.isNaN(offsetNum)) {
          throw new Error('Initial Meter Reading must be a number.');
        }
      }
      // Custom multiplier validation — positive integer only.
      let customMultNum: number | undefined;
      if (isSalt && useCustomMultiplier) {
        const trimmed = customMultiplier.trim();
        if (trimmed === '') throw new Error('Custom multiplier divisor is required.');
        const parsed = Number(trimmed);
        if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
          throw new Error('Custom multiplier must be a positive integer.');
        }
        customMultNum = parsed;
      }

      await onSave({
        notes,
        externalMeterModel: isSalt ? (externalMeterModel.trim() || undefined) : undefined,
        externalMeterSerial: isSalt ? (externalMeterSerial.trim() || undefined) : undefined,
        mechanicalOffset: offsetNum,
        // SALT-only fields. For non-SALT devices we leave these
        // undefined so the partial-update on the backend skips them.
        connectedMeterType: isSalt ? (connectedMeterType || undefined) : undefined,
        connectedMeterTypeCustom: isSalt && connectedMeterType === ConnectedMeterType.Other
          ? (connectedMeterTypeCustom.trim() || undefined)
          : undefined,
        customPulseMultiplier: isSalt ? customMultNum : undefined,
        // Empty string clears the override (UpdateDeviceModel checks for empty/whitespace);
        // any other value sets it.
        customPulseUnitOfMeasure: isSalt && useCustomUnitOfMeasure
          ? (customUnitOfMeasure.trim() || '')
          : undefined,
      });
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message || 'Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title="Edit Device" description={`${device.serialNumber} — ${device.devEui}`}>
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        {isSalt && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Pulse configuration
            </p>
            <div className="grid grid-cols-2 gap-4">
              {/* Multiplier — standard dropdown OR custom integer
                  divisor. Custom checkbox is below the input so the
                  swap is obvious. */}
              <div>
                {useCustomMultiplier ? (
                  <Input
                    label="Multiplier (custom)"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    step={1}
                    value={customMultiplier}
                    onChange={(e) => setCustomMultiplier(e.target.value)}
                    placeholder="e.g. 50 → renders as 1:50"
                  />
                ) : (
                  <Select
                    label="Multiplier"
                    value={String(multiplier)}
                    onChange={(v) => setMultiplier(Number(v) as DeviceMultiplier)}
                    options={SALT_MULTIPLIER_OPTIONS}
                  />
                )}
                <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={useCustomMultiplier}
                    onChange={(e) => setUseCustomMultiplier(e.target.checked)}
                    className="w-3.5 h-3.5"
                  />
                  Custom multiplier (1:N)
                </label>
              </div>
              {/* Unit of measure — standard dropdown OR custom string. */}
              <div>
                {useCustomUnitOfMeasure ? (
                  <Input
                    label="Unit of Measure (custom)"
                    value={customUnitOfMeasure}
                    onChange={(e) => setCustomUnitOfMeasure(e.target.value)}
                    placeholder="e.g. psi, lbs, °F"
                    maxLength={64}
                  />
                ) : (
                  <Select
                    label="Unit of Measure"
                    value={String(unitOfMeasure)}
                    onChange={(v) => setUnitOfMeasure(Number(v) as DeviceUnitOfMeasure)}
                    options={uomOptions}
                  />
                )}
                <label className="mt-1.5 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={useCustomUnitOfMeasure}
                    onChange={(e) => setUseCustomUnitOfMeasure(e.target.checked)}
                    className="w-3.5 h-3.5"
                  />
                  Custom unit of measure
                </label>
              </div>
            </div>
            <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
              Applied to every reading at display time — historical readings
              re-label without rewriting stored values. Custom values are
              scoped to this device only.
            </p>
          </div>
        )}

        {/* Connected meter type — SALT only. Fixed enum + free-text
            override when "Other" is picked. */}
        {isSalt && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Connected To
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Meter type"
                value={connectedMeterType}
                onChange={(v) => setConnectedMeterType((v || '') as ConnectedMeterType | '')}
                placeholder="Choose what this is wired to..."
                options={[
                  { value: ConnectedMeterType.Gas, label: connectedMeterTypeDisplayName(ConnectedMeterType.Gas) },
                  { value: ConnectedMeterType.Electric, label: connectedMeterTypeDisplayName(ConnectedMeterType.Electric) },
                  { value: ConnectedMeterType.Water, label: connectedMeterTypeDisplayName(ConnectedMeterType.Water) },
                  { value: ConnectedMeterType.Runtime, label: connectedMeterTypeDisplayName(ConnectedMeterType.Runtime) },
                  { value: ConnectedMeterType.Other, label: connectedMeterTypeDisplayName(ConnectedMeterType.Other) },
                ]}
              />
              {connectedMeterType === ConnectedMeterType.Other && (
                <Input
                  label="Custom label"
                  value={connectedMeterTypeCustom}
                  onChange={(e) => setConnectedMeterTypeCustom(e.target.value)}
                  placeholder="e.g., Pressure Sensor"
                  maxLength={100}
                />
              )}
            </div>
          </div>
        )}

        {/* Connected meter + Initial Meter Reading — both SALT-only. uMeter
            is itself a meter, not connected to one, and the initial-dial
            offset is no longer surfaced for regular meters (data stays
            dormant on legacy rows). */}
        {isSalt && (
          <div>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Connected meter
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Connected meter model"
                placeholder="e.g., Versamag"
                value={externalMeterModel}
                onChange={(e) => setExternalMeterModel(e.target.value)}
              />
              <Input
                label="Connected meter serial"
                placeholder="e.g., VM-12345"
                value={externalMeterSerial}
                onChange={(e) => setExternalMeterSerial(e.target.value)}
              />
              <Input
                label="Initial Meter Reading"
                placeholder="auto-captured on first transmission"
                type="number"
                inputMode="decimal"
                step="any"
                value={mechanicalOffset}
                onChange={(e) => setMechanicalOffset(e.target.value)}
                helperText="Auto-captured on the device's first transmission so the displayed total starts at 0. Subtracted from the raw count before the multiplier — leave alone unless correcting an over-count."
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Add notes about this device..."
            className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
          />
        </div>

        {submitError && <p className="text-sm text-error-600">{submitError}</p>}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">Save Changes</Button>
        </div>
      </form>
    </Modal>
  );
}
